import { IFadeTrackConf } from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import { NolimitAudioPlayer } from "@nolimitcity/slot-audio/bin/NolimitAudioPlayer";
import { NolimitHowl } from "@nolimitcity/slot-audio/bin/NolimitHowl";
import { NolimitSlotAudio } from "@nolimitcity/slot-audio/bin/NolimitSlotAudio";
import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { NamedLogger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { gsap } from "gsap";

import { EventsManager, SoundEvents } from "../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { AbstractModuleController } from "../../../abstracts/modules/AbstractModuleController";
import { SoundConfig } from "../../../abstracts/types/interfaces";
import { getRandomBetween } from "../../../abstracts/utils/JSUtils";
import { playSound } from "../../../abstracts/utils/SoundUtils";
import { DDD } from "../../DDD";
import { DDDSoundNames } from "../../assetsLoader/DDDAssetsNames";
import { ambientalSoundSettings, devDebugConfig } from "../../config/DDDConfig";
import { DDDSoundDebugger } from "../../utils/DDDSoundDebugger";

export let respinMusic = { isPlaying: false };

export class DDDSoundController extends AbstractModuleController {
    private player: NolimitAudioPlayer;
    private currentMusic: string;
    private gameMode: AbstractGameMode;
    private logger: NamedLogger;
    private ambientTimeline: gsap.core.Timeline = gsap.timeline();
    private currentAmbientSound: string = ``;
    private ambienceIsOn: boolean = true;
    private ambianceIsPaused: boolean = false;
    private soundsLoaded: boolean = false;
    private playOnceSoundsList: SoundConfig[] = [];
    private mainSoundAlreadyPlaying: boolean = false;

    public get name(): string {
        return `DDDSoundController`;
    }

    protected init() {
        super.init();

        this.player = SlotGame.sound as NolimitAudioPlayer;
        this.logger = new NamedLogger(`DDDSound`);
    }

    protected handleView() {
        super.handleView();

        this.view.isDisabled = true;
    }

    protected handleEvents() {
        super.handleEvents();

        EventsManager.on(
            SoundEvents.PLAY_BACKGROUND_MUSIC,
            (gameMode: AbstractGameMode | undefined) => {
                let newGameMode = gameMode;
                if (newGameMode === undefined) {
                    newGameMode = this.gameMode;
                    this.currentMusic = ``;
                }
                this.switchMusic(newGameMode);
                this.startAmbientSounds();
            }
        );

        EventsManager.on(SoundEvents.STOP_BACKGROUND_MUSIC, () => {
            this.stopSound({ name: this.currentMusic });
            this.currentMusic = ``;
            this.ambienceIsOn = false;
            this.mainSoundAlreadyPlaying = false;
        });

        EventsManager.on(SoundEvents.PLAY_SOUND, (soundConfig: SoundConfig) => {
            this.playSound(soundConfig);
        });

        EventsManager.on(SoundEvents.PLAY_SOUND_ONCE, (soundConfig: SoundConfig) => {
            this.playSoundOnce(soundConfig);
        });

        EventsManager.on(SoundEvents.STOP_SOUND, (soundConfig: SoundConfig) => {
            this.stopSound(soundConfig);
        });

        EventsManager.on(SoundEvents.FADE_OUT_BACKGROUND_MUSIC, (duration: number = 100) => {
            this.fadeSound({ name: this.currentMusic, duration: duration, from: 1, to: 0 });

            this.fadeOutAmbientSounds();
        });

        EventsManager.on(SoundEvents.FADE_IN_BACKGROUND_MUSIC, (duration: number = 100) => {
            this.fadeSound({ name: this.currentMusic, duration: duration, from: 0, to: 1 });

            this.fadeInAmbientSounds();
        });

        EventsManager.on(SoundEvents.FADE_SOUND, (fadeConfig: SoundConfig) => {
            this.fadeSound(fadeConfig);
        });

        EventsManager.on(SoundEvents.FADE_AND_STOP_SOUND, (fadeConfig: SoundConfig) => {
            this.fadeAndStopSound(fadeConfig);
        });

        EventsManager.on(SoundEvents.START_AMBIANCE_SOUNDS, () => {
            this.ambienceIsOn = true;
            this.ambianceIsPaused = false;
            this.playAmbientSound();
        });

        EventsManager.on(SoundEvents.STOP_AMBIANCE_SOUNDS, () => {
            this.ambienceIsOn = false;
            this.ambianceIsPaused = true;
            this.killAmbTl();
        });

        NolimitSlotAudio.apiPlugIn.settings.on(APISetting.SFX, (value: any) => {
            if (value) {
                this.soundsLoaded = true;
                this.onGameModeUpdate(DDD.model.data.nextMode);
            }
        });
    }

    private playSound(soundConfig: SoundConfig) {
        if (
            devDebugConfig.showSoundDebugger &&
            !DDDSoundDebugger.soundIsEnabled(soundConfig.name)
        ) {
            return;
        }

        const track: NolimitHowl | undefined = this.player.playTrack(soundConfig);
        if (track && soundConfig.stereo) {
            track.player.stereo(soundConfig.stereo);
        }
        this.logger.log(`playSound: ${soundConfig.name}`);
    }

    private playSoundOnce(soundConfig: SoundConfig) {
        let soundToPlay: SoundConfig | undefined = this.playOnceSoundsList.find(
            (playOnceConfig) => playOnceConfig.name === soundConfig.name
        );
        if (soundToPlay === undefined) {
            soundToPlay = soundConfig;
            this.playOnceSoundsList.push(soundConfig);
        }

        const currentTime = Date.now();
        if (soundToPlay.lastTimePlayed === undefined) {
            this.playSound(soundToPlay);
            soundToPlay.lastTimePlayed = currentTime;
            return;
        }

        const elapsedTime = Math.floor((Date.now() - soundToPlay.lastTimePlayed!) / 1000);

        if (elapsedTime >= soundToPlay.playOnceMinDuration!) {
            this.playSound(soundToPlay);
            soundToPlay.lastTimePlayed = currentTime;
        }
    }

    private stopSound(soundConfig: SoundConfig) {
        this.player.stopTrack(soundConfig.name);
        this.logger.log(`stopSound: ${soundConfig.name}`);
    }

    private fadeSound(soundConfig: SoundConfig) {
        this.player.fadeTrack(soundConfig as IFadeTrackConf);
        this.logger.log(
            `fadeSound: ${soundConfig.name} from ${soundConfig.from} to ${soundConfig.to} in ${soundConfig.duration}ms`
        );
    }

    private fadeAndStopSound(soundConfig: SoundConfig) {
        this.player.fadeTrack(soundConfig as IFadeTrackConf);
        gsap.delayedCall(soundConfig.duration! / 1000, () => {
            this.stopSound(soundConfig);
        });
        this.logger.log(
            `fadeSound: ${soundConfig.name} from ${soundConfig.from} to ${soundConfig.to} in ${soundConfig.duration}ms`
        );
    }

    private switchMusic(mode: AbstractGameMode | string) {
        let nextMusic;

        switch (mode) {
            case AbstractGameMode.FREESPIN:
                nextMusic = DDDSoundNames.FREESPIN_MUSIC;
                break;

            case AbstractGameMode.SUPER_FREESPIN:
                nextMusic = DDDSoundNames.SUPER_FREESPIN_MUSIC;
                break;

            // case AbstractGameMode.RESPIN:
            //     nextMusic = DDDSoundNames.XNUDGE_RESPIN_MUSIC;
            //     break;

            default:
                nextMusic = DDDSoundNames.MAIN_MUSIC;
                break;
        }

        if (nextMusic === this.currentMusic) {
            return;
        }

        this.player.stopTrack(this.currentMusic);

        this.currentMusic = nextMusic;

        if (this.gameMode === AbstractGameMode.RESPIN) {
            this.playSound({ name: nextMusic, volume: 1, repeat: 999 });
        } else {
            this.playSound({ name: nextMusic, volume: 1, repeat: undefined });
        }
    }

    protected startAmbientSounds() {
        this.ambienceIsOn = true;

        gsap.delayedCall(0.1, () => {
            if (!this.mainSoundAlreadyPlaying) {
                this.playSound({ name: DDDSoundNames.MAIN_AMBIENT, repeat: 999999, volume: 0 });
                this.fadeSound({
                    name: DDDSoundNames.MAIN_AMBIENT,
                    duration: 1000,
                    from: 0,
                    to: 1,
                });
                this.mainSoundAlreadyPlaying = true;
            }

            if (!this.ambianceIsPaused) {
                this.playAmbientSound();
            }
        });
    }

    protected fadeInAmbientSounds() {
        if (!this.ambienceIsOn) {
            this.fadeSound({ name: DDDSoundNames.MAIN_AMBIENT, duration: 1, from: 0, to: 1 });
        }

        this.ambienceIsOn = true;
    }

    protected fadeOutAmbientSounds() {
        this.fadeSound({ name: DDDSoundNames.MAIN_AMBIENT, duration: 0.5, from: 1, to: 0 });

        this.fadeSound({ name: this.currentAmbientSound, duration: 0.5, from: 1, to: 0 });

        this.ambienceIsOn = false;
    }

    private killAmbTl() {
        if (this.ambientTimeline !== undefined) {
            this.ambientTimeline.pause();
            this.ambientTimeline.kill();
            this.ambientTimeline.clear();
        }
    }

    private playAmbientSound(): void {
        const currentSoundIndex: number[] = [0, 0, 0];

        this.killAmbTl();

        const generateTimeline = () => {
            this.killAmbTl();
            this.ambientTimeline = gsap.timeline({
                onComplete: () => {
                    generateTimeline();
                },
            });
            let timelineDuration: number = 0;

            ambientalSoundSettings.forEach((soundSettings, index) => {
                const delayDuration = getRandomBetween(
                    soundSettings.timeFrame[0],
                    soundSettings.timeFrame[1]
                );

                this.currentAmbientSound = soundSettings.soundsList[currentSoundIndex[index]];

                if (this.ambienceIsOn) {
                    this.ambientTimeline.call(
                        playSound,
                        [
                            {
                                name: this.currentAmbientSound,
                            },
                        ],
                        delayDuration
                    );
                }

                timelineDuration =
                    delayDuration > timelineDuration ? delayDuration : timelineDuration;

                currentSoundIndex[index]++;
                if (currentSoundIndex[index] > soundSettings.soundsList.length - 1) {
                    currentSoundIndex[index] = 0;
                }
            });

            this.ambientTimeline.totalDuration(timelineDuration);
        };

        generateTimeline();
    }

    protected onGameModeUpdate(gameMode: AbstractGameMode) {
        if (gameMode === this.gameMode || !this.soundsLoaded) {
            return;
        }

        this.gameMode = gameMode;

        this.startAmbientSounds();
    }
}
